import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, XCircle, MapPin, Navigation, Shield } from 'lucide-react';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';

// Public page the receipt QR points to. Confirms a receipt is genuine by showing
// the trip's real details straight from SafariCab's servers - so a forged PDF
// can't match a non-existent / altered trip.
const VerifyReceipt: React.FC = () => {
  const { rideId } = useParams<{ rideId: string }>();
  const [trip, setTrip] = useState<any>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'invalid'>('loading');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/public/trip/${rideId}`);
        if (!res.ok) { setState('invalid'); return; }
        setTrip(await res.json());
        setState('ok');
      } catch { setState('invalid'); }
    })();
  }, [rideId]);

  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-safaricab-darker flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-safaricab-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (state === 'invalid') {
    return (
      <div className="min-h-screen bg-safaricab-darker flex flex-col items-center justify-center px-6 text-center">
        <XCircle className="w-16 h-16 text-red-500 mb-4" />
        <h1 className="text-white font-bold text-xl mb-1">Receipt not recognised</h1>
        <p className="text-white/50 text-sm">This receipt could not be verified against SafariCab records. It may be invalid or forged.</p>
      </div>
    );
  }

  const fare = Number(trip.price || 0);
  const date = trip.completedAt || trip.createdAt;
  const Row = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-start justify-between py-2 border-b border-white/5">
      <span className="text-white/50 text-sm">{label}</span>
      <span className="text-white text-sm font-medium text-right max-w-[60%]">{value}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-safaricab-darker flex flex-col">
      <div className="px-4 pt-[calc(var(--sai-top)+1rem)] pb-3 flex items-center gap-2 bg-safaricab-dark">
        <Shield className="w-5 h-5 text-safaricab-gold" />
        <span className="text-white font-bold text-sm">SafariCab · Receipt verification</span>
      </div>

      <div className="flex-1 px-4 py-6">
        <div className="flex flex-col items-center text-center mb-6">
          <CheckCircle className="w-14 h-14 text-green-500 mb-2" />
          <h1 className="text-white font-bold text-xl">Genuine SafariCab receipt</h1>
          <p className="text-white/50 text-sm">These details come directly from SafariCab's servers. Check they match your receipt.</p>
        </div>

        <div className="bg-safaricab-card rounded-2xl p-4 border border-white/10">
          <Row label="Receipt ID" value={String(rideId)} />
          {date && <Row label="Date" value={new Date(date).toLocaleString()} />}
          <Row label="Status" value={trip.status === 'completed' ? 'Completed' : trip.status} />
          <Row label="Fare paid" value={`KES ${fare.toLocaleString()}`} />
          {trip.distance ? <Row label="Distance" value={`${Math.round(trip.distance * 10) / 10} km`} /> : null}
          {trip.driver?.name && <Row label="Driver" value={trip.driver.name} />}
          {trip.vehicle && (
            <Row label="Vehicle" value={[trip.vehicle.make, trip.vehicle.color, trip.vehicle.plate_number].filter(Boolean).join(' · ')} />
          )}
        </div>

        <div className="mt-4 space-y-2 bg-safaricab-card rounded-2xl p-4 border border-white/10">
          <div className="flex items-center gap-2 text-sm"><MapPin className="w-4 h-4 text-green-500 flex-shrink-0" /><span className="text-white/80 truncate">{trip.pickup?.address || '-'}</span></div>
          <div className="flex items-center gap-2 text-sm"><Navigation className="w-4 h-4 text-red-500 flex-shrink-0" /><span className="text-white/80 truncate">{trip.dropoff?.address || '-'}</span></div>
        </div>

        <p className="text-white/30 text-[11px] text-center mt-6">Verified by SafariCab. If the details above don't match your receipt, the receipt may have been altered.</p>
      </div>
    </div>
  );
};

export default VerifyReceipt;
